// externals
import type { Reducer } from 'redux';

import { initActionTypes } from '@makemydeal/dr-shared-store';
import type { TradeInsScopedState } from '@makemydeal/dr-dash-types';

// consts/enums
import * as tradesActionTypes from '../actionTypes/tradesActionTypes';

/**
 * Scoped reducer for multiple trade-ins scenario-specific state.
 * Contains only scenario-specific properties like which trade-ins are included in the offer.
 * The actual trade-in data (VIN, make, model, etc.) remains in the global tradeIns reducer.
 */
export type { TradeInsScopedState } from '@makemydeal/dr-dash-types';

export const initialState: TradeInsScopedState = {
    shouldIncludeTradeIns: [true] // Start with one trade-in included by default
};

// Multi trade-in data comes from the payload's `trades.tradeIns` (not `trade.tradeIns`)
export const buildTradeInsScopedStateFromTrades = (
    state: TradeInsScopedState,
    trades?: { tradeIns?: { shouldIncludeTradeIn?: boolean }[] }
): TradeInsScopedState => {
    const tradeIns = trades?.tradeIns;
    if (tradeIns && Array.isArray(tradeIns) && tradeIns.length > 0) {
        return {
            ...state,
            shouldIncludeTradeIns: tradeIns.map((tradeIn) => tradeIn.shouldIncludeTradeIn ?? true)
        };
    }
    return state;
};

export type TradeInsScopedReducer = Reducer<TradeInsScopedState>;

export const reducer: TradeInsScopedReducer = (state = initialState, action: any) => {
    switch (action.type) {
        case initActionTypes.INIT_PENCIL_SUCCESS: {
            return buildTradeInsScopedStateFromTrades(state, action.payload.trades);
        }
        case tradesActionTypes.INCLUDE_TRADE_INS_CHANGED: {
            const { shouldIncludeTradeIn, index } = action.payload;
            const newShouldIncludeTradeIns = [...state.shouldIncludeTradeIns];

            // Ensure array is large enough
            while (newShouldIncludeTradeIns.length <= index) {
                newShouldIncludeTradeIns.push(true);
            }

            newShouldIncludeTradeIns[index] = shouldIncludeTradeIn;

            return {
                ...state,
                shouldIncludeTradeIns: newShouldIncludeTradeIns
            };
        }
        default: {
            return state;
        }
    }
};
